import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  Visibility as ViewIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import customYoutubeApi from '../api/customYoutubeApi';

// MIDI note structure: pitch classes with their enharmonic options
const MIDI_NOTES_STRUCTURE = {
  'C': { natural: 'C', flat: null, sharp: null },
  'C#/Db': { natural: null, flat: 'Db', sharp: 'C#' },
  'D': { natural: 'D', flat: null, sharp: null },
  'D#/Eb': { natural: null, flat: 'Eb', sharp: 'D#' },
  'E/Fb': { natural: 'E', flat: 'Fb', sharp: null },
  'F/E#': { natural: 'F', flat: null, sharp: 'E#' },
  'F#/Gb': { natural: null, flat: 'Gb', sharp: 'F#' },
  'G': { natural: 'G', flat: null, sharp: null },
  'G#/Ab': { natural: null, flat: 'Ab', sharp: 'G#' },
  'A': { natural: 'A', flat: null, sharp: null },
  'A#/Bb': { natural: null, flat: 'Bb', sharp: 'A#' },
  'B/Cb': { natural: 'B', flat: 'Cb', sharp: null }
};

const OCTAVES = [3, 4, 5];
const TONAL_SECTION_COLORS = ['blue', 'red', 'green'];

// Helper function to construct note name from pitch class and octave
const constructNoteName = (pitchClass, octave, useFlat) => {
  if (!pitchClass || octave === null || octave === undefined) return '';
  
  const noteInfo = MIDI_NOTES_STRUCTURE[pitchClass];
  if (!noteInfo) return '';
  
  // Determine which notation to use - priority to user's sharp/flat preference
  let noteName;
  if (useFlat && noteInfo.flat) {
    // User wants flat and flat exists
    noteName = noteInfo.flat;
  } else if (!useFlat && noteInfo.sharp) {
    // User wants sharp and sharp exists
    noteName = noteInfo.sharp;
  } else if (noteInfo.natural) {
    // Natural note or no enharmonic option for user's preference
    noteName = noteInfo.natural;
  } else {
    // Fallback to whatever is available
    noteName = noteInfo.flat || noteInfo.sharp;
  }
  
  return noteName + octave;
};

// Helper function to parse a note string into its components
const parseNoteName = (noteName) => {
  if (!noteName) return { pitchClass: '', octave: null, useFlat: false };
  
  const match = noteName.match(/^([A-G][#b]?)([0-9])$/);
  if (!match) return { pitchClass: '', octave: null, useFlat: false };
  
  const note = match[1];
  const octave = parseInt(match[2]);
  const useFlat = note.includes('b');
  
  // Find the pitch class
  let pitchClass = '';
  for (const [key, value] of Object.entries(MIDI_NOTES_STRUCTURE)) {
    if (value.natural === note || value.sharp === note || value.flat === note) {
      pitchClass = key;
      break;
    }
  }
  
  return { pitchClass, octave, useFlat };
};

const AdminCustomYoutubeVideos = () => {
  const { hasPermission } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    videoId: '',
    startPoint: 0,
    endPoint1: 30,
    endPoint2: 60,
    hasEndPoint2: false,
    // MIDI Note 1
    midiNote1PitchClass: '',
    midiNote1Octave: 4,
    midiNote1UseFlat: false,
    hasMidiNote1: false,
    // MIDI Note 2
    midiNote2PitchClass: '',
    midiNote2Octave: 4,
    midiNote2UseFlat: false,
    hasMidiNote2: false,
    tonalKeySections: [],
    isActive: true,
    isPublic: true,
    tags: ''
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    if (hasPermission('admin')) {
      loadVideos();
    }
  }, [hasPermission]);

  // Check admin permission
  if (!hasPermission('admin')) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. This page requires admin privileges.
        </Alert>
      </Box>
    );
  }

  const loadVideos = async () => {
    try {
      setLoading(true);
      const response = await customYoutubeApi.admin.getAll();
      setVideos(response.data);
    } catch (err) {
      console.error('Error loading videos:', err);
      setError(`Failed to load videos: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      videoId: '',
      startPoint: 0,
      endPoint1: 30,
      endPoint2: 60,
      hasEndPoint2: false,
      // MIDI Note 1
      midiNote1PitchClass: '',
      midiNote1Octave: 4,
      midiNote1UseFlat: false,
      hasMidiNote1: false,
      // MIDI Note 2
      midiNote2PitchClass: '',
      midiNote2Octave: 4,
      midiNote2UseFlat: false,
      hasMidiNote2: false,
      tonalKeySections: [],
      isActive: true,
      isPublic: true,
      tags: ''
    });
    setEditingVideo(null);
  };

  const handleOpenDialog = (video = null) => {
    if (video) {
      setEditingVideo(video);
      
      // Parse MIDI notes if they exist
      const midiNote1Info = parseNoteName(video.midiNote1);
      const midiNote2Info = parseNoteName(video.midiNote2);
      
      setFormData({
        title: video.title,
        description: video.description || '',
        videoId: video.videoId,
        // MIDI Note 1
        midiNote1PitchClass: midiNote1Info.pitchClass,
        midiNote1Octave: midiNote1Info.octave || 4,
        midiNote1UseFlat: midiNote1Info.useFlat,
        hasMidiNote1: !!video.midiNote1,
        // MIDI Note 2
        midiNote2PitchClass: midiNote2Info.pitchClass,
        midiNote2Octave: midiNote2Info.octave || 4,
        midiNote2UseFlat: midiNote2Info.useFlat,
        hasMidiNote2: !!video.midiNote2,
        tonalKeySections: video.tonalKeySections || [],
        startPoint: video.startPoint,
        endPoint1: video.endPoint1,
        endPoint2: video.endPoint2 || 60,
        hasEndPoint2: video.endPoint2 !== undefined && video.endPoint2 !== null && video.endPoint2 > 0,
        isActive: video.isActive,
        isPublic: video.isPublic || false,
        tags: video.tags ? video.tags.join(', ') : ''
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Tonal Key Section Handlers
  const handleAddTonalKeySection = () => {
    setFormData(prev => ({
      ...prev,
      tonalKeySections: [
        ...prev.tonalKeySections,
        { startTime: 0, endTime: 30, key: 'C Major', color: 'blue' }
      ]
    }));
  };

  const handleRemoveTonalKeySection = (index) => {
    setFormData(prev => ({
      ...prev,
      tonalKeySections: prev.tonalKeySections.filter((_, i) => i !== index)
    }));
  };

  const handleTonalKeySectionChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      tonalKeySections: prev.tonalKeySections.map((section, i) => 
        i === index ? { 
          ...section, 
          [field]: (field === 'key' || field === 'color') ? value : Number(value) 
        } : section
      )
    }));
  };

  const handleSubmit = async () => {
    try {
      const videoData = {
        ...formData
      };
      
      // Construct MIDI note strings from components
      const midiNote1 = formData.hasMidiNote1 
        ? constructNoteName(formData.midiNote1PitchClass, formData.midiNote1Octave, formData.midiNote1UseFlat)
        : null;
      const midiNote2 = formData.hasMidiNote2 
        ? constructNoteName(formData.midiNote2PitchClass, formData.midiNote2Octave, formData.midiNote2UseFlat)
        : null;
      
      videoData.midiNote1 = midiNote1;
      videoData.midiNote2 = midiNote2;
      
      // Remove UI-only fields from the data sent to backend
      delete videoData.hasEndPoint2;
      delete videoData.hasMidiNote1;
      delete videoData.hasMidiNote2;
      delete videoData.midiNote1PitchClass;
      delete videoData.midiNote1Octave;
      delete videoData.midiNote1UseFlat;
      delete videoData.midiNote2PitchClass;
      delete videoData.midiNote2Octave;
      delete videoData.midiNote2UseFlat;
      
      // If hasEndPoint2 is false, explicitly set endPoint2 to null to remove it
      if (!formData.hasEndPoint2) {
        videoData.endPoint2 = null;
      }

      if (editingVideo) {
        await customYoutubeApi.admin.update(editingVideo.customVideoId, videoData);
      } else {
        await customYoutubeApi.admin.create(videoData);
      }

      await loadVideos();
      handleCloseDialog();
      setError(null);
    } catch (err) {
      console.error('Error saving video:', err);
      setError(`Failed to save video: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleDelete = async (video) => {
    if (window.confirm(`Are you sure you want to delete "${video.title}"?`)) {
      try {
        await customYoutubeApi.admin.delete(video.customVideoId);
        await loadVideos();
        setError(null);
      } catch (err) {
        console.error('Error deleting video:', err);
        setError(`Failed to delete video: ${err.message}`);
      }
    }
  };

  const handleViewQR = async (video) => {
    try {
      const response = await customYoutubeApi.getQRCode(video.customVideoId);
      setQrData(response.data);
      setQrDialogOpen(true);
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError(`Failed to generate QR code: ${err.message}`);
    }
  };

  const handleCopyUrl = async (customVideoId) => {
    const url = `https://media.amit-klein.com/custom-youtube-video/${customVideoId}`;
    try {
      await navigator.clipboard.writeText(url);
      setSnackbarMessage('URL copied to clipboard!');
      setSnackbarOpen(true);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setSnackbarMessage('URL copied to clipboard!');
        setSnackbarOpen(true);
      } catch (fallbackErr) {
        console.error('Failed to copy URL:', fallbackErr);
        setSnackbarMessage('Failed to copy URL');
        setSnackbarOpen(true);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleViewCustomVideo = (customVideoId) => {
    window.open(`/custom-youtube-video/${customVideoId}`, '_blank');
  };

  const handleDownloadQR = () => {
    if (qrData?.qrCodeDataUrl) {
      const link = document.createElement('a');
      link.download = `qr-${qrData.customVideoId}.png`;
      link.href = qrData.qrCodeDataUrl;
      link.click();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          🎬 Custom YouTube Videos Admin
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add New Video
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Video ID</TableCell>
              <TableCell>Custom URL</TableCell>
              <TableCell>Timing</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {videos.map((video) => (
              <TableRow key={video._id}>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2">{video.title}</Typography>
                    {video.description && (
                      <Typography variant="caption" color="text.secondary">
                        {video.description.substring(0, 50)}...
                      </Typography>
                    )}
                    <Box sx={{ mt: 0.5 }}>
                      {video.tags?.map((tag) => (
                        <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {video.videoId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {video.customVideoId?.substring(0, 8)}...
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                    https://media.amit-klein.com/custom-youtube-video/{video.customVideoId}
                  </Typography>
                  <br />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleCopyUrl(video.customVideoId)}
                    sx={{ mt: 0.5 }}
                  >
                    Copy URL
                  </Button>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    Start: {video.startPoint}s<br />
                    End1: {video.endPoint1}s<br />
                    {video.endPoint2 !== undefined && video.endPoint2 !== null && (
                      <>End2: {video.endPoint2}s</>
                    )}
                    {(video.endPoint2 === undefined || video.endPoint2 === null) && (
                      <Typography variant="caption" color="text.secondary">No End2</Typography>
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Chip 
                      label={video.isActive ? 'Active' : 'Inactive'} 
                      color={video.isActive ? 'success' : 'default'} 
                      size="small" 
                    />
                    <Chip 
                      label={video.isPublic ? 'Public' : 'Private'} 
                      color={video.isPublic ? 'info' : 'default'} 
                      size="small" 
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {new Date(video.createdAt).toLocaleDateString()}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    by {video.userName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenDialog(video)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleViewCustomVideo(video.customVideoId)}
                      title="View Custom Video"
                      color="primary"
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleViewQR(video)}
                      title="QR Code"
                    >
                      <QrCodeIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => window.open(`https://www.youtube.com/watch?v=${video.videoId}`, '_blank')}
                      title="View Original on YouTube"
                    >
                      <PlayIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDelete(video)}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Video Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingVideo ? 'Edit Video' : 'Add New Video'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={handleInputChange('title')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleInputChange('description')}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="YouTube Video ID"
                value={formData.videoId}
                onChange={handleInputChange('videoId')}
                required
                helperText="e.g., dQw4w9WgXcQ (from https://www.youtube.com/watch?v=dQw4w9WgXcQ)"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Start Point (seconds)"
                type="number"
                value={formData.startPoint}
                onChange={handleInputChange('startPoint')}
                required
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="End Point 1 (seconds)"
                type="number"
                value={formData.endPoint1}
                onChange={handleInputChange('endPoint1')}
                required
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hasEndPoint2}
                    onChange={handleInputChange('hasEndPoint2')}
                  />
                }
                label="Enable End Point 2"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="End Point 2 (seconds)"
                type="number"
                value={formData.endPoint2}
                onChange={handleInputChange('endPoint2')}
                disabled={!formData.hasEndPoint2}
                helperText={formData.hasEndPoint2 ? "Optional second endpoint" : "Disabled - toggle to enable"}
              />
            </Grid>
            
            {/* MIDI Note 1 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                🎵 MIDI Notes (Optional)
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hasMidiNote1}
                    onChange={handleInputChange('hasMidiNote1')}
                  />
                }
                label="Enable MIDI Note 1"
              />
            </Grid>
            <Grid item xs={5}>
              <FormControl fullWidth disabled={!formData.hasMidiNote1}>
                <InputLabel>Pitch Class</InputLabel>
                <Select
                  value={formData.midiNote1PitchClass}
                  onChange={handleInputChange('midiNote1PitchClass')}
                  label="Pitch Class"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {Object.keys(MIDI_NOTES_STRUCTURE).map((pitchClass) => (
                    <MenuItem key={pitchClass} value={pitchClass}>
                      {pitchClass}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth disabled={!formData.hasMidiNote1}>
                <InputLabel>Octave</InputLabel>
                <Select
                  value={formData.midiNote1Octave}
                  onChange={handleInputChange('midiNote1Octave')}
                  label="Octave"
                >
                  {OCTAVES.map((octave) => (
                    <MenuItem key={octave} value={octave}>
                      {octave}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.midiNote1UseFlat}
                    onChange={handleInputChange('midiNote1UseFlat')}
                    disabled={!formData.hasMidiNote1 || !formData.midiNote1PitchClass || 
                      (!MIDI_NOTES_STRUCTURE[formData.midiNote1PitchClass]?.flat && 
                       !MIDI_NOTES_STRUCTURE[formData.midiNote1PitchClass]?.sharp)}
                  />
                }
                label={formData.midiNote1UseFlat ? "Use ♭ (Flat)" : "Use ♯ (Sharp)"}
              />
              {formData.hasMidiNote1 && formData.midiNote1PitchClass && (
                <Typography variant="caption" display="block" color="primary">
                  Selected: <strong>{constructNoteName(formData.midiNote1PitchClass, formData.midiNote1Octave, formData.midiNote1UseFlat)}</strong>
                </Typography>
              )}
            </Grid>
            
            {/* MIDI Note 2 */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hasMidiNote2}
                    onChange={handleInputChange('hasMidiNote2')}
                  />
                }
                label="Enable MIDI Note 2"
              />
            </Grid>
            <Grid item xs={5}>
              <FormControl fullWidth disabled={!formData.hasMidiNote2}>
                <InputLabel>Pitch Class</InputLabel>
                <Select
                  value={formData.midiNote2PitchClass}
                  onChange={handleInputChange('midiNote2PitchClass')}
                  label="Pitch Class"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {Object.keys(MIDI_NOTES_STRUCTURE).map((pitchClass) => (
                    <MenuItem key={pitchClass} value={pitchClass}>
                      {pitchClass}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth disabled={!formData.hasMidiNote2}>
                <InputLabel>Octave</InputLabel>
                <Select
                  value={formData.midiNote2Octave}
                  onChange={handleInputChange('midiNote2Octave')}
                  label="Octave"
                >
                  {OCTAVES.map((octave) => (
                    <MenuItem key={octave} value={octave}>
                      {octave}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.midiNote2UseFlat}
                    onChange={handleInputChange('midiNote2UseFlat')}
                    disabled={!formData.hasMidiNote2 || !formData.midiNote2PitchClass || 
                      (!MIDI_NOTES_STRUCTURE[formData.midiNote2PitchClass]?.flat && 
                       !MIDI_NOTES_STRUCTURE[formData.midiNote2PitchClass]?.sharp)}
                  />
                }
                label={formData.midiNote2UseFlat ? "Use ♭ (Flat)" : "Use ♯ (Sharp)"}
              />
              {formData.hasMidiNote2 && formData.midiNote2PitchClass && (
                <Typography variant="caption" display="block" color="primary">
                  Selected: <strong>{constructNoteName(formData.midiNote2PitchClass, formData.midiNote2Octave, formData.midiNote2UseFlat)}</strong>
                </Typography>
              )}
            </Grid>
            
            {/* Tonal Key Sections */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                🎼 Tonal Key Sections (Optional)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Define the tonal key for different time sections of the video
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
                {formData.tonalKeySections.map((section, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ minWidth: 30 }}>
                      {index + 1}.
                    </Typography>
                    <TextField
                      size="small"
                      label="Start (s)"
                      type="number"
                      value={section.startTime}
                      onChange={(e) => handleTonalKeySectionChange(index, 'startTime', e.target.value)}
                      sx={{ width: 90 }}
                      inputProps={{ step: 0.1, min: 0 }}
                    />
                    <TextField
                      size="small"
                      label="End (s)"
                      type="number"
                      value={section.endTime}
                      onChange={(e) => handleTonalKeySectionChange(index, 'endTime', e.target.value)}
                      sx={{ width: 90 }}
                      inputProps={{ step: 0.1, min: 0 }}
                    />
                    <TextField
                      size="small"
                      label="Key"
                      value={section.key}
                      onChange={(e) => handleTonalKeySectionChange(index, 'key', e.target.value)}
                      placeholder="e.g., C Major, G Minor"
                      sx={{ flex: 1, minWidth: 120 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel>Color</InputLabel>
                      <Select
                        value={section.color || 'blue'}
                        onChange={(e) => handleTonalKeySectionChange(index, 'color', e.target.value)}
                        label="Color"
                      >
                        {TONAL_SECTION_COLORS.map((color) => {
                          const colorMap = {
                            'blue': '#1976d2',
                            'red': '#d32f2f',
                            'green': '#2e7d32'
                          };
                          return (
                            <MenuItem key={color} value={color}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box 
                                  sx={{ 
                                    width: 16, 
                                    height: 16, 
                                    borderRadius: 1,
                                    bgcolor: colorMap[color],
                                    border: '1px solid #ccc'
                                  }} 
                                />
                                {color.charAt(0).toUpperCase() + color.slice(1)}
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <IconButton 
                      size="small" 
                      onClick={() => handleRemoveTonalKeySection(index)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
              <Button 
                variant="outlined" 
                size="small"
                onClick={handleAddTonalKeySection}
                startIcon={<AddIcon />}
              >
                Add Key Section
              </Button>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags (comma-separated)"
                value={formData.tags}
                onChange={handleInputChange('tags')}
                helperText="e.g., music, tutorial, entertainment"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleInputChange('isActive')}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPublic}
                    onChange={handleInputChange('isPublic')}
                  />
                }
                label="Public"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingVideo ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm">
        <DialogTitle>QR Code for Video</DialogTitle>
        <DialogContent>
          {qrData && (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <img 
                src={qrData.qrCodeDataUrl} 
                alt="QR Code" 
                style={{ maxWidth: '100%', height: 'auto' }}
              />
              <Typography variant="h6" sx={{ mt: 2 }}>
                {qrData.videoTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {qrData.videoUrl}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Video ID: {qrData.customVideoId}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
          <Button onClick={handleDownloadQR} variant="contained">
            Download QR Code
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default AdminCustomYoutubeVideos;